import NewMessageSound from 'src/assets/audios/new-message.mp3';

/**
 * The one "something arrived" sound: invites, chat messages, support replies.
 *
 * Chrome will not load a page's first clip while its tab is hidden. It holds
 * the clip, play() included, until the tab is shown, so an invite that landed
 * in a background tab rang minutes later, as the learner came back to a game
 * long gone. A clip loaded while the page was in front plays in the background
 * straight away, so it is loaded early, and a background play that would only
 * be held back is skipped.
 *
 * Even a loaded clip is sometimes held in a tab that has been in the
 * background a while, so a play that has not started within a moment, or by
 * the time the tab is switched, is cancelled rather than left to ring on the
 * learner's return. It also keeps one stuck play from holding up every tab.
 *
 * Every open tab has its own socket, so one invite reaches each of them and
 * would ring once per tab. The tabs queue for one browser-wide lock, and the
 * tab that rings takes a second lock and keeps it a moment. The next tab in
 * line sees that one held and stays quiet; a tab whose play was refused or
 * cancelled takes nothing, so the next in line rings instead. Locks are the
 * shared state on purpose: localStorage reaches other tabs late, so a tab
 * queued right behind the one that rang can still read the old value.
 */

const CHIME_LOCK = 'abekus-chime';
const CHIME_RANG_LOCK = 'abekus-chime-rang';
// One socket event reaches every tab within milliseconds, so anything inside
// this window after a chime is the same event, or close enough to share it.
const CHIME_HOLD_MS = 2_000;
// How long a play may take to start before it is cancelled.
const CHIME_START_MS = 1_000;

let chime: HTMLAudioElement | null = null;

const getChime = () => {
  if (!chime) {
    chime = new Audio(NewMessageSound);
    chime.preload = 'auto';
  }
  return chime;
};

/** Load the clip now, while the page is most likely in front. */
export const warmChime = () => {
  getChime();
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/** Resolves true once the clip is playing; false if refused or cancelled. */
const ring = () =>
  new Promise<boolean>((resolve) => {
    const audio = getChime();
    const listening = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const finish = (rang: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      listening.abort();
      resolve(rang);
    };
    const cancel = () => {
      if (settled) return;
      audio.pause();
      finish(false);
    };
    timer = setTimeout(cancel, CHIME_START_MS);
    // Shown: a held play would start now, late. Hidden: it may be held next.
    document.addEventListener('visibilitychange', cancel, {
      signal: listening.signal,
    });
    audio.currentTime = 0;
    // Blocked autoplay is fine to swallow: the toast or badge still shows it.
    audio.play().then(
      () => finish(true),
      () => finish(false),
    );
  });

export const playChime = () => {
  const audio = getChime();
  if (document.hidden && audio.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA)
    return;
  if (!navigator.locks) {
    ring();
    return;
  }
  navigator.locks
    .request(CHIME_LOCK, async () => {
      const { held = [] } = await navigator.locks.query();
      if (held.some((lock) => lock.name === CHIME_RANG_LOCK)) return;
      if (!(await ring())) return;
      // Taken before the queue lock is let go, so the next tab in line sees it.
      await new Promise<void>((taken) => {
        navigator.locks
          .request(CHIME_RANG_LOCK, () => {
            taken();
            return sleep(CHIME_HOLD_MS);
          })
          // Never leave the queue lock held because this one failed.
          .catch(() => taken());
      });
    })
    .catch(() => {});
};
